import { c as _c } from "react/compiler-runtime";
import type { MarketingDeliveryDiscipline } from '@sizzlenz/shared';
import { MARKETING_DELIVERY_ACTIVITIES, getEntityCapabilityForDeliveryActivity } from '@sizzlenz/shared';
import Link from 'next/link';
import { useState } from 'react';
import { ChevronDown, ChevronUp, Lightbulb, Users, Wrench } from 'lucide-react';
import { buildDirectoryHref } from '@/lib/directory-filters';
import { buildInspirationFeedHref, TEXT } from './shared';

/** Number of execution/delivery cards shown before the grid is expanded. */
const INITIAL_VISIBLE_SERVICES = 9;
const getExecutionStyleCategory = (disciplineId: MarketingDeliveryDiscipline) => {
  switch (disciplineId) {
    case 'creative-design':
      return 'creative';
    case 'production':
      return 'production';
    case 'logistics-distribution':
      return 'logistics';
    case 'activation-staffing':
      return 'activation';
    default:
      return 'strategy';
  }
};
const executionServices = MARKETING_DELIVERY_ACTIVITIES.map(activity => {
  const capability = getEntityCapabilityForDeliveryActivity(activity.id);
  return {
    id: activity.id,
    name: activity.label,
    category: getExecutionStyleCategory(activity.disciplineId),
    examplesHref: buildInspirationFeedHref('execution', activity.id),
    // Providers link only exists when the activity maps to a directory capability.
    providersHref: capability ? buildDirectoryHref('capability', capability) : undefined
  };
});

/**
 * "Make it happen" framework block: strategy vs execution framing plus the grid
 * of execution/delivery service cards linking to inspiration + providers.
 */
export function DemandMakeItHappenBlock() {
  const $ = _c(29);
  const [expanded, setExpanded] = useState(false);
  const hasMore = executionServices.length > INITIAL_VISIBLE_SERVICES;
  let hiddenCount;
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== expanded) {
    const visibleServices = expanded ? executionServices : executionServices.slice(0, INITIAL_VISIBLE_SERVICES);
    hiddenCount = executionServices.length - INITIAL_VISIBLE_SERVICES;
    t5 = "overflow-hidden border-2 border-gray-950 bg-white";
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div className="flex items-start gap-3 border-b-2 border-gray-950 bg-[#f3eee7] px-5 py-5"><Wrench className="h-5 w-5 shrink-0 text-[#d63f20]" aria-hidden="true" /><div><p className="text-2xl font-black tracking-[-0.03em] sm:text-3xl">Make it happen</p></div></div>;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    t2 = "p-5 sm:p-6";
    let t7;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="border-2 border-gray-950 bg-[#211d4f] px-5 py-4 text-white"><p className={`${TEXT.cardLabel} text-white/70`}>Strategy + insights</p><p className={`mt-1 ${TEXT.cardHeadline}`}>Understanding shoppers and choosing the right approach.</p><p className={`${TEXT.cardBody} text-white/60`}>Consumer insights, journey mapping, channel strategy, campaign planning.</p></div>;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="mb-5 grid gap-3 sm:grid-cols-2">{t7}<div className="border-2 border-gray-950 bg-[#f3eee7] px-5 py-4"><p className={`${TEXT.cardLabel} text-[#6f4410]`}>Execution + delivery</p><p className={`mt-1 ${TEXT.cardHeadline}`}>Implementing those strategies and campaigns in the real world.</p><p className={`${TEXT.cardBody} text-gray-600`}>The services below turn ideas into things people can see, touch, try or buy.</p></div></div>;
      t4 = <p className={`mb-4 max-w-3xl ${TEXT.sectionDescription} text-gray-600`}>A good idea only creates demand once it's live in the real world. These are the hands-on activities that turn a plan into something people can see, touch, try or buy, from designing the display and producing the assets to staffing the activation and measuring what worked. You can handle them in-house, do them yourself, or work with specialist partners who take them on end-to-end. Explore each one for real examples and the providers who deliver it.</p>;
      $[11] = t3;
      $[12] = t4;
    } else {
      t3 = $[11];
      t4 = $[12];
    }
    t0 = "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3";
    t1 = visibleServices.map(_temp);
    $[0] = expanded;
    $[1] = hiddenCount;
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    hiddenCount = $[1];
    t0 = $[2];
    t1 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
  }
  let t7;
  if ($[13] !== t0 || $[14] !== t1) {
    t7 = <div className={t0}>{t1}</div>;
    $[13] = t0;
    $[14] = t1;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== expanded || $[17] !== hiddenCount) {
    t8 = hasMore && <div className="mt-4 flex justify-center"><button type="button" onClick={() => setExpanded(_temp2)} aria-expanded={expanded} className="inline-flex cursor-pointer items-center gap-1.5 border-2 border-gray-950 bg-white px-4 py-2 text-xs font-black uppercase tracking-[0.08em] text-gray-950 outline-none transition-colors hover:bg-gray-950 hover:text-white focus-visible:ring-2 focus-visible:ring-[#211d4f]">{expanded ? <>Show fewer<ChevronUp className="h-3.5 w-3.5" aria-hidden="true" /></> : <>Show {hiddenCount} more<ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /></>}</button></div>;
    $[16] = expanded;
    $[17] = hiddenCount;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t2 || $[20] !== t3 || $[21] !== t4 || $[22] !== t7 || $[23] !== t8) {
    t9 = <div className={t2}>{t3}{t4}{t7}{t8}</div>;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== t5 || $[26] !== t6 || $[27] !== t9) {
    t10 = <div className={t5}>{t6}{t9}</div>;
    $[25] = t5;
    $[26] = t6;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
}
function _temp2(prev) {
  return !prev;
}
function _temp(service) {
  return <div key={service.id} className={`group flex flex-col border px-5 py-4 transition-transform hover:-translate-y-0.5 hover:border-gray-950 ${service.category === "creative" ? "border-[#eeeafd] bg-[#eeeafd]/50 text-[#4a3b8a]" : service.category === "production" ? "border-[#fff1e9] bg-[#fff1e9]/50 text-[#9a4420]" : service.category === "logistics" ? "border-[#e9f9f3] bg-[#e9f9f3]/50 text-[#1a6b5c]" : service.category === "activation" ? "border-[#fff8d9] bg-[#fff8d9]/50 text-[#6f4410]" : "border-gray-300 bg-gray-50 text-gray-700"}`}><p className={`${TEXT.servicePill}`}>{service.name}</p><div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-gray-950/10 pt-3"><Link href={service.examplesHref} className="inline-flex cursor-pointer items-center gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#d63f20] outline-none hover:underline focus-visible:ring-2 focus-visible:ring-[#211d4f]"><Lightbulb className="h-3.5 w-3.5" aria-hidden="true" />Inspiration</Link>{service.providersHref && <Link href={service.providersHref} className="inline-flex cursor-pointer items-center gap-1 text-xs font-black uppercase tracking-[0.08em] text-[#211d4f] outline-none hover:underline focus-visible:ring-2 focus-visible:ring-[#211d4f]"><Users className="h-3.5 w-3.5" aria-hidden="true" />Who can help</Link>}</div></div>;
}